import axios from "axios";
import AppConsts from "../../app/AppConsts";
import {httpClient} from "../../utils/httpClient";

const BASE_PATH = `${AppConsts.API_PATH}/pre-hospital-workflows/dlv`;

export const MAX_CASES_PER_REQUEST = 10;

export class DlvApiError extends Error {
    constructor(message, field) {
        super(message);
        this.field = field;
    }
}

const GENERIC_ERROR = "Qualcosa è andato storto: riprova tra qualche istante.";

// The http client resolves 400s with the error response and 401s with undefined (the user is signed out)
function unwrap(response) {
    if (!response) {
        throw new DlvApiError("La sessione è scaduta: accedi di nuovo.");
    }
    if (response.data?.errors?.length) {
        const error = response.data.errors[0];
        throw new DlvApiError(error.message ?? GENERIC_ERROR, error.details?.field);
    }
    return response.data;
}

const INVALID_RESPONSE_ERROR = "La risposta del server non è quella attesa: controlla che il backend sia aggiornato.";

// An outdated backend answers unknown routes with the app's index.html and a 200: check the shape of what arrives
async function call(request, isValid) {
    let response;
    try {
        response = await request;
    } catch (e) {
        if (axios.isCancel(e)) {
            throw e;
        }
        throw new DlvApiError(GENERIC_ERROR);
    }

    const data = unwrap(response);
    if (!isValid(data)) {
        throw new DlvApiError(INVALID_RESPONSE_ERROR);
    }
    return data;
}

const isProgramsResponse = (data) => Array.isArray(data?.programs) && typeof data.defaultProgram === 'string';

export function fetchPrograms() {
    return call(httpClient.get(`${BASE_PATH}/programs`), isProgramsResponse);
}

export function fetchProgramContent(name) {
    return call(httpClient.get(`${BASE_PATH}/programs/content`, {params: {name}}),
        (data) => typeof data?.content === 'string');
}

export function uploadProgram({name, content, overwrite}) {
    return call(httpClient.post(`${BASE_PATH}/programs`, {name, content, overwrite}), isProgramsResponse);
}

export function deleteProgram(name) {
    return call(httpClient.delete(`${BASE_PATH}/programs`, {params: {name}}), isProgramsResponse);
}

export function setDefaultProgram(name) {
    return call(httpClient.put(`${BASE_PATH}/programs/default`, {name}), isProgramsResponse);
}

export function fetchProgramTests(name) {
    return call(httpClient.get(`${BASE_PATH}/programs/tests`, {params: {name}}),
        (data) => typeof data?.content === 'string');
}

export function saveProgramTests(name, content) {
    return call(httpClient.put(`${BASE_PATH}/programs/tests`, {name, content}),
        (data) => typeof data?.content === 'string');
}

export function runTestsChunk({programName, cases, cancelToken}) {
    return call(httpClient.post(`${BASE_PATH}/tests`, {programName, cases}, {cancelToken}),
        (data) => Array.isArray(data?.results));
}
